import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Paper } from "@material-ui/core";
import IconButton from "@material-ui/core/IconButton";
import {
  Close as CloseIcon,
  AutoAwesome,
  PictureAsPdf,
} from "@mui/icons-material";
import Empty from "antd/es/empty";
import { useDispatch } from "react-redux";
import { Button, Modal } from "../../components";
import Menus from "../../components/Menus";
import { dateTimeFormatter, Toaster } from "../../utils/common";
import { recordMatchesClientCategory } from "../../utils/clientDetailsCategory";
import {
  openDocumentInNewTab,
  openDocumentPreviewTab,
} from "../../utils/openDocumentInNewTab";
import { DOCUMENT_ACTION } from "../../redux/action";
import {
  getClientTaxStrategies,
  createClientTaxStrategy,
  updateClientTaxStrategy,
  saveClientTaxStrategy,
  sendClientTaxStrategy,
  deleteClientTaxStrategy,
  generateTaxStrategyAI,
  getTaxStrategyComments,
  createTaxStrategyComment,
} from "../../redux/action/taxStrategyAction";

const AI_MODES = [
  { id: "improve", label: "Improve my draft" },
  { id: "generate", label: "Write from prompt" },
];

const PREVIEW_MAX_CHARS = 140;

function getEmployeeName(employeeId, activeEmployees) {
  if (!employeeId || !activeEmployees?.length) return "Employee";
  const emp = activeEmployees.find((e) => e.employee_id === employeeId);
  if (!emp) return "Employee";
  return `${emp.first_name || ""} ${emp.last_name || ""}`.trim() || "Employee";
}

function getStrategyId(strategy) {
  return strategy?.tax_strategy_id || strategy?.strategy_id || strategy?.id;
}

function getPlanDisplayName(strategy) {
  const named = String(strategy?.plan_name || "").trim();
  if (named) return named;
  const doc = String(strategy?.document_name || "")
    .replace(/\.pdf$/i, "")
    .trim();
  if (doc) return doc;
  return "Tax Plan";
}

function getPlanDeliveryStatus(strategy) {
  const status = String(strategy?.delivery_status || "sent").trim().toLowerCase();
  if (status === "preview" || status === "saved" || status === "sent") return status;
  return "sent";
}

function truncatePlanText(text, max = PREVIEW_MAX_CHARS) {
  const cleaned = String(text || "")
    .replace(/\r\n/g, "\n")
    .replace(/#{1,6}\s+/g, "")
    .replace(/\s+/g, " ")
    .trim();
  if (!cleaned) return "No preview available";
  if (cleaned.length <= max) return cleaned;
  return `${cleaned.slice(0, max).trim()}…`;
}

const TaxStrategySection = ({
  clientId,
  clientData,
  activeEmployees = [],
  loggedInUserId,
  userRole,
  category,
}) => {
  const dispatch = useDispatch();
  const [strategiesList, setStrategiesList] = useState([]);
  const [planName, setPlanName] = useState("");
  const [strategyText, setStrategyText] = useState("");
  const [aiMode, setAiMode] = useState("improve");
  const [aiPrompt, setAiPrompt] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isGeneratingAI, setIsGeneratingAI] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [editModal, setEditModal] = useState({
    open: false,
    strategyId: null,
    planName: "",
    text: "",
  });
  const [commentsModal, setCommentsModal] = useState({
    open: false,
    strategyId: null,
    strategyPreview: "",
    planName: "",
  });
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState("");
  const [isLoadingComments, setIsLoadingComments] = useState(false);
  const [isSavingComment, setIsSavingComment] = useState(false);
  const [viewingPdfId, setViewingPdfId] = useState(null);
  const [previewModal, setPreviewModal] = useState({
    open: false,
    strategy: null,
    blobUrl: null,
    loading: false,
  });
  const [isSavingDelivery, setIsSavingDelivery] = useState(false);
  const [isSendingPlan, setIsSendingPlan] = useState(false);
  const previewUrlRef = useRef(null);

  const isStaff = userRole !== "client";

  const clientLabel = clientData
    ? `${clientData.first_name || ""} ${clientData.last_name || ""}`.trim()
    : "Client";

  const loadStrategies = useCallback(async () => {
    if (!clientId) return;
    try {
      const data = await dispatch(
        getClientTaxStrategies({
          clientId,
          ...(category ? { category } : {}),
        }),
      ).unwrap();
      setStrategiesList(Array.isArray(data) ? data : []);
    } catch (error) {
      setStrategiesList([]);
    }
  }, [clientId, category, dispatch]);

  useEffect(() => {
    if (clientId) {
      loadStrategies();
    }
  }, [clientId, loadStrategies, category]);

  useEffect(() => {
    setStrategyText("");
    setPlanName("");
    setAiPrompt("");
    setEditModal({ open: false, strategyId: null, planName: "", text: "" });
    setCommentsModal({
      open: false,
      strategyId: null,
      strategyPreview: "",
      planName: "",
    });
    setComments([]);
    setCommentText("");
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
    setPreviewModal({
      open: false,
      strategy: null,
      blobUrl: null,
      loading: false,
    });
  }, [category]);

  const filteredStrategies = useMemo(() => {
    if (!category) return strategiesList;
    return strategiesList.filter((strategy) =>
      recordMatchesClientCategory(strategy, category),
    );
  }, [strategiesList, category]);

  const sortedStrategies = useMemo(
    () =>
      [...filteredStrategies]
        .filter((strategy) =>
          isStaff ? true : getPlanDeliveryStatus(strategy) === "sent",
        )
        .sort((a, b) => {
          const aTime = new Date(a.createdAt || a.updatedAt || 0).getTime();
          const bTime = new Date(b.createdAt || b.updatedAt || 0).getTime();
          return bTime - aTime;
        }),
    [filteredStrategies, isStaff],
  );

  const handleGenerateAI = async () => {
    if (!clientId) {
      Toaster.error("Client ID not found");
      return;
    }

    const draft = strategyText.replace(/\r\n/g, "\n").trim();
    const prompt = aiPrompt.replace(/\r\n/g, "\n").trim();

    if (aiMode === "improve" && !draft && !prompt) {
      Toaster.error(
        "Enter instructions in the AI advisor or a plan to improve.",
      );
      return;
    }

    if (aiMode === "generate" && !prompt) {
      Toaster.warning("Enter a prompt describing what the tax plan should cover.");
      return;
    }

    setIsGeneratingAI(true);
    try {
      const result = await generateTaxStrategyAI({
        client_id: clientId,
        mode: aiMode,
        draft,
        prompt,
        ...(category ? { category } : {}),
      });

      if (result?.status === 1 && result?.data?.strategy) {
        setStrategyText(String(result.data.strategy));
        Toaster.success("AI suggestion ready — review and send when ready.");
      } else {
        Toaster.error(result?.message || "Failed to generate tax plan");
      }
    } catch (error) {
      Toaster.error("AI request failed. Please try again.");
    } finally {
      setIsGeneratingAI(false);
    }
  };

  const closePreview = () => {
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
    setPreviewModal({
      open: false,
      strategy: null,
      blobUrl: null,
      loading: false,
    });
  };

  const openPlanPreview = async (strategy) => {
    const fileURL = strategy?.document_link;
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
    setPreviewModal({
      open: true,
      strategy,
      blobUrl: null,
      loading: Boolean(fileURL),
    });
    if (!fileURL) return;

    try {
      const response = await DOCUMENT_ACTION.downloadViewFile({
        fileURL,
        document_name: strategy.document_name || "Tax-Plan.pdf",
        document_type: "application/pdf",
        type: "Preview",
      });
      const blobUrl = response?.raw || null;
      previewUrlRef.current = blobUrl;
      setPreviewModal({
        open: true,
        strategy,
        blobUrl,
        loading: false,
      });
      if (!blobUrl) {
        Toaster.error("Unable to preview tax plan PDF");
      }
    } catch (error) {
      console.error(error);
      setPreviewModal({
        open: true,
        strategy,
        blobUrl: null,
        loading: false,
      });
      Toaster.error("Unable to preview tax plan PDF");
    }
  };

  const handleCreateStrategy = async () => {
    const name = planName.trim();
    const payload = strategyText.replace(/\r\n/g, "\n").trim();
    if (!name) {
      Toaster.warning("Please enter a plan name");
      return;
    }
    if (!payload) {
      Toaster.warning("Please enter a tax plan");
      return;
    }
    if (!clientId) {
      Toaster.error("Client ID not found");
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await createClientTaxStrategy({
        client_id: clientId,
        strategy: payload,
        plan_name: name,
        ...(category ? { category } : {}),
      });

      if (result?.data?.status === 1) {
        const created = result?.data?.data;
        setStrategyText("");
        setPlanName("");
        setAiPrompt("");
        await loadStrategies();
        if (created) {
          await openPlanPreview(created);
        }
      } else {
        Toaster.error(result?.data?.message || "Failed to create tax plan");
      }
    } catch (error) {
      console.error(error);
      Toaster.error("Error creating tax plan. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteStrategy = async (strategy) => {
    const strategyId = getStrategyId(strategy);
    if (!strategyId) return;

    const confirmed = window.confirm(
      "Delete this tax plan and its PDF from storage?",
    );
    if (!confirmed) return;

    try {
      const result = await deleteClientTaxStrategy(strategyId);
      if (result?.data?.status === 1) {
        Toaster.success("Tax plan deleted");
        await loadStrategies();
      } else {
        Toaster.error(result?.data?.message || "Failed to delete tax plan");
      }
    } catch (error) {
      Toaster.error("Error deleting tax plan.");
    }
  };

  const handleSaveEdit = async () => {
    const nextName = String(editModal.planName || "").trim();
    const nextText = String(editModal.text || "").replace(/\r\n/g, "\n").trim();
    if (!nextName) {
      Toaster.warning("Please enter a plan name");
      return;
    }
    if (!nextText) {
      Toaster.warning("Please enter a tax plan");
      return;
    }
    if (!editModal.strategyId) {
      Toaster.error("Plan ID not found");
      return;
    }

    setIsUpdating(true);
    try {
      const result = await updateClientTaxStrategy({
        strategy_id: editModal.strategyId,
        strategy: nextText,
        plan_name: nextName,
      });

      if (result?.data?.status === 1) {
        const updated = result?.data?.data;
        Toaster.success("Tax plan PDF updated");
        setEditModal({ open: false, strategyId: null, planName: "", text: "" });
        await loadStrategies();
        if (
          updated &&
          previewModal.open &&
          getStrategyId(previewModal.strategy) === getStrategyId(updated)
        ) {
          await openPlanPreview(updated);
        }
      } else {
        Toaster.error(result?.data?.message || "Failed to update tax plan");
      }
    } catch (error) {
      console.error(error);
      Toaster.error("Error updating tax plan.");
    } finally {
      setIsUpdating(false);
    }
  };

  const handleSaveDelivery = async (strategy) => {
    const strategyId = getStrategyId(strategy);
    if (!strategyId) return;
    setIsSavingDelivery(true);
    try {
      const result = await saveClientTaxStrategy(strategyId);
      if (result?.data?.status === 1) {
        Toaster.success("Tax plan saved");
        closePreview();
        await loadStrategies();
      } else {
        Toaster.error(result?.data?.message || "Failed to save tax plan");
      }
    } catch (error) {
      console.error(error);
      Toaster.error("Error saving tax plan.");
    } finally {
      setIsSavingDelivery(false);
    }
  };

  const handleSendDelivery = async (strategy) => {
    const strategyId = getStrategyId(strategy);
    if (!strategyId) return;
    setIsSendingPlan(true);
    try {
      const result = await sendClientTaxStrategy(strategyId);
      if (result?.data?.status === 1) {
        Toaster.success("Tax plan sent to the client");
        closePreview();
        await loadStrategies();
      } else {
        Toaster.error(result?.data?.message || "Failed to send tax plan");
      }
    } catch (error) {
      console.error(error);
      Toaster.error("Error sending tax plan.");
    } finally {
      setIsSendingPlan(false);
    }
  };

  const handleViewPdf = async (strategy) => {
    const strategyId = getStrategyId(strategy);
    const fileURL = strategy?.document_link;
    if (!fileURL) {
      Toaster.warning("PDF is not available for this plan yet.");
      return;
    }

    const previewWindow = openDocumentPreviewTab();
    setViewingPdfId(strategyId);
    try {
      const response = await DOCUMENT_ACTION.downloadViewFile({
        fileURL,
        document_name: strategy.document_name || "Tax-Plan.pdf",
        document_type: "application/pdf",
        type: "Preview",
      });
      const blobUrl = response?.raw;
      if (!blobUrl) {
        Toaster.error("Unable to open tax plan PDF");
        if (previewWindow && !previewWindow.closed) previewWindow.close();
        return;
      }

      await openDocumentInNewTab(
        blobUrl,
        "application/pdf",
        strategy.document_name || "Tax-Plan.pdf",
        previewWindow,
      );
    } catch (error) {
      console.error(error);
      Toaster.error("Unable to open tax plan PDF");
      if (previewWindow && !previewWindow.closed) previewWindow.close();
    } finally {
      setViewingPdfId(null);
    }
  };

  const openComments = async (strategy) => {
    const strategyId = getStrategyId(strategy);
    if (!strategyId) return;
    setCommentsModal({
      open: true,
      strategyId,
      strategyPreview: truncatePlanText(strategy.strategy || "", 240),
      planName: getPlanDisplayName(strategy),
    });
    setCommentText("");
    setIsLoadingComments(true);
    try {
      const rows = await getTaxStrategyComments(strategyId);
      setComments(Array.isArray(rows) ? rows : []);
    } catch {
      setComments([]);
    } finally {
      setIsLoadingComments(false);
    }
  };

  const openEditModal = (strategy) => {
    setEditModal({
      open: true,
      strategyId: getStrategyId(strategy),
      planName: getPlanDisplayName(strategy),
      text: String(
        strategy.strategy || strategy.content || strategy.text || "",
      ),
    });
  };

  const handlePlanMenuClick = (option, strategy) => {
    if (option === "Edit") openEditModal(strategy);
    else if (option === "Save") handleSaveDelivery(strategy);
    else if (option === "Send") handleSendDelivery(strategy);
    else if (option === "Comment") openComments(strategy);
    else if (option === "Delete") handleDeleteStrategy(strategy);
  };

  const getMenuOptions = (strategy) => {
    if (!isStaff) return ["Comment"];
    const canModify =
      userRole === "admin" ||
      strategy.created_by === loggedInUserId ||
      strategy.employee_id === loggedInUserId;
    if (!canModify) return ["Comment"];
    const status = getPlanDeliveryStatus(strategy);
    if (status === "saved") return ["Edit", "Send", "Comment", "Delete"];
    if (status === "preview") return ["Edit", "Save", "Send", "Comment", "Delete"];
    return ["Edit", "Comment", "Delete"];
  };

  const handleSaveComment = async () => {
    const text = commentText.replace(/\r\n/g, "\n").trim();
    if (!text) {
      Toaster.warning("Please enter a comment");
      return;
    }
    if (!commentsModal.strategyId) return;

    setIsSavingComment(true);
    try {
      const result = await createTaxStrategyComment({
        tax_strategy_id: commentsModal.strategyId,
        comment: text,
      });
      if (result?.data?.status === 1) {
        Toaster.success("Comment added");
        setCommentText("");
        const rows = await getTaxStrategyComments(commentsModal.strategyId);
        setComments(Array.isArray(rows) ? rows : []);
        await loadStrategies();
      } else {
        Toaster.error(result?.data?.message || "Failed to add comment");
      }
    } catch {
      Toaster.error("Error adding comment");
    } finally {
      setIsSavingComment(false);
    }
  };

  const renderStrategyList = (title) => (
    <div className="tax-strategy-plans-panel">
      <div className="tax-strategy-plans-panel-header">
        <h4 className="tax-strategy-plans-panel-title">{title}</h4>
      </div>
      <div className="tax-plan-cards-grid">
        {sortedStrategies.map((strategy) => {
          const strategyId = getStrategyId(strategy);
          const employeeName =
            strategy.employee_name ||
            getEmployeeName(
              strategy.employee_id || strategy.created_by,
              activeEmployees,
            );
          const advisorLabel = isStaff
            ? employeeName
            : employeeName || "Your Tax Advisor";
          const hasPdf = Boolean(strategy.document_link);
          const commentCount = Number(strategy.comment_count || 0);
          const displayName = getPlanDisplayName(strategy);
          const preview = truncatePlanText(
            strategy.strategy || strategy.content || strategy.text || "",
          );
          const menuOptions = getMenuOptions(strategy);

          return (
            <div key={strategyId} className="tax-plan-file-card">
              <div className="tax-plan-file-card-header">
                <div className="tax-plan-file-card-title-wrap">
                  <PictureAsPdf
                    className="tax-plan-file-card-pdf-icon"
                    fontSize="small"
                  />
                  <span className="tax-plan-file-card-name" title={displayName}>
                    {displayName}
                  </span>
                </div>
                <Menus
                  options={menuOptions}
                  onMenuClick={(option) =>
                    handlePlanMenuClick(option, strategy)
                  }
                />
              </div>

              <button
                type="button"
                className="tax-plan-file-card-body"
                onClick={() => handleViewPdf(strategy)}
                disabled={!hasPdf || viewingPdfId === strategyId}
                title={hasPdf ? "Open PDF" : "PDF pending"}
              >
                <p className="tax-plan-file-card-preview">{preview}</p>
                <div className="tax-plan-file-card-meta">
                  <span>
                    {isStaff ? advisorLabel : `From: ${advisorLabel}`}
                  </span>
                  <span>
                    {dateTimeFormatter(strategy.createdAt || strategy.updatedAt)}
                  </span>
                </div>
                <div className="tax-plan-file-card-badges">
                  {isStaff && getPlanDeliveryStatus(strategy) === "saved" ? (
                    <span className="tax-strategy-pdf-badge is-saved">Saved</span>
                  ) : null}
                  {hasPdf ? (
                    <span className="tax-strategy-pdf-badge">PDF</span>
                  ) : (
                    <span className="tax-strategy-pdf-badge is-missing">
                      PDF pending
                    </span>
                  )}
                  {commentCount > 0 && (
                    <span className="tax-strategy-comment-count">
                      {commentCount} comment{commentCount === 1 ? "" : "s"}
                    </span>
                  )}
                </div>
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );

  const commentsModalUi = commentsModal.open ? (
    <Modal
      open={commentsModal.open}
      className="edit-note-modal timesheet-view-modal"
      handleClose={() =>
        isSavingComment
          ? null
          : setCommentsModal({
              open: false,
              strategyId: null,
              strategyPreview: "",
              planName: "",
            })
      }
      maxWidth
      title={null}
    >
      <div className="edit-note-wrapper">
        <div className="edit-note-header">
          <h2 className="edit-note-title">
            {commentsModal.planName
              ? `Comments · ${commentsModal.planName}`
              : "Tax plan comments"}
          </h2>
          <IconButton
            className="edit-note-close-btn"
            onClick={() =>
              isSavingComment
                ? null
                : setCommentsModal({
                    open: false,
                    strategyId: null,
                    strategyPreview: "",
                    planName: "",
                  })
            }
            size="small"
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
        </div>

        {commentsModal.strategyPreview ? (
          <p className="tax-strategy-comment-preview">
            {commentsModal.strategyPreview}
          </p>
        ) : null}

        <div className="tax-strategy-comments-list">
          {isLoadingComments ? (
            <p className="tax-strategy-comments-empty">Loading comments…</p>
          ) : comments.length === 0 ? (
            <p className="tax-strategy-comments-empty">No comments yet</p>
          ) : (
            comments.map((row) => (
              <div
                key={row.tax_strategy_comment_id}
                className="tax-strategy-comment-item"
              >
                <div className="tax-strategy-comment-meta">
                  <strong>
                    {row.author_name ||
                      (row.author_role === "client" ? "Client" : "Advisor")}
                  </strong>
                  <span>{dateTimeFormatter(row.createdAt)}</span>
                </div>
                <p>{row.comment}</p>
              </div>
            ))
          )}
        </div>

        <div className="comments-input-section" style={{ marginBottom: 0 }}>
          <textarea
            className="comments-textarea"
            placeholder="Add a comment about this tax plan…"
            value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
            rows={4}
            disabled={isSavingComment}
          />
          <div
            className="comments-actions"
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: "10px",
            }}
          >
            <Button
              text={isSavingComment ? "Posting..." : "Post comment"}
              disable={isSavingComment}
              handleClick={handleSaveComment}
              large
            />
          </div>
        </div>
      </div>
    </Modal>
  ) : null;

  if (!isStaff) {
    return (
      <div className="client-details-tax-strategy client-tax-strategy-view">
        <Paper className="tax-strategy-card">
          <div className="tax-strategy-header">
            {/* <h3 className="tax-strategy-title">Tax Planning</h3> */}
            <p className="tax-strategy-subtitle">
              View your tax plan PDFs and leave comments for your advisor.
            </p>
          </div>
          {sortedStrategies.length > 0 ? (
            renderStrategyList("Your Tax Plans")
          ) : (
            <div className="comments-empty">
              <Empty description="No tax plans have been shared with you yet" />
            </div>
          )}
        </Paper>
        {commentsModalUi}
      </div>
    );
  }

  return (
    <div className="client-details-tax-strategy">
      <Paper className="tax-strategy-card">
        <div className="tax-strategy-header">
          <h3 className="tax-strategy-title">Tax Planning</h3>
          <p className="tax-strategy-subtitle">
            Document tax planning recommendations for {clientLabel || "this client"}.
            Creating builds a branded FinloTax PDF so you can preview it, then edit, save
            it for employees, or send it to the client. Editing regenerates and replaces
            that PDF.
          </p>
        </div>

        <div className="tax-strategy-compose">
          <label className="tax-strategy-field-label" htmlFor="tax-strategy-name">
            Plan name
          </label>
          <input
            id="tax-strategy-name"
            className="comments-textarea tax-strategy-name-input"
            type="text"
            placeholder="e.g. 2025 IRA planning"
            value={planName}
            onChange={(e) => setPlanName(e.target.value)}
            maxLength={120}
            disabled={isSubmitting || isGeneratingAI}
          />

          <label className="tax-strategy-field-label" htmlFor="tax-strategy-text">
            Plan
          </label>
          <textarea
            id="tax-strategy-text"
            className="comments-textarea tax-strategy-textarea"
            placeholder="Type the tax plan here..."
            value={strategyText}
            onChange={(e) => setStrategyText(e.target.value)}
            rows={8}
            disabled={isSubmitting || isGeneratingAI}
          />

          <div className="tax-strategy-ai-panel">
            <div className="tax-strategy-ai-panel-header">
              <AutoAwesome className="tax-strategy-ai-icon" fontSize="small" />
              <span className="tax-strategy-ai-title">AI Tax Advisor</span>
            </div>
            <p className="tax-strategy-ai-help">
              AI acts as an experienced US tax advisor. Results are suggestions only —
              review before sending to the client record.
            </p>

            <div className="tax-strategy-ai-mode">
              {AI_MODES.map((mode) => (
                <button
                  key={mode.id}
                  type="button"
                  className={`tax-strategy-ai-mode-btn ${
                    aiMode === mode.id ? "is-active" : ""
                  }`}
                  onClick={() => setAiMode(mode.id)}
                  disabled={isGeneratingAI}
                >
                  {mode.label}
                </button>
              ))}
            </div>

            <textarea
              className="comments-textarea tax-strategy-ai-prompt"
              placeholder={
                aiMode === "improve"
                  ? "Optional: extra instructions for AI (e.g. focus on retirement planning, S-corp election)..."
                  : "Describe what you want the AI to write (e.g. 2025 tax-saving plan for W-2 employee with rental income)..."
              }
              value={aiPrompt}
              onChange={(e) => setAiPrompt(e.target.value)}
              rows={3}
              disabled={isGeneratingAI}
            />

            <div className="tax-strategy-ai-actions">
              <Button
                text={isGeneratingAI ? "Generating..." : "Generate with AI"}
                icon={<AutoAwesome />}
                handleClick={handleGenerateAI}
                disable={isGeneratingAI || isSubmitting}
                variant="outline"
              />
            </div>
          </div>

          <div className="comments-actions tax-strategy-send-actions">
            <Button
              text={isSubmitting ? "Creating PDF..." : "Create Plan"}
              handleClick={handleCreateStrategy}
              disable={isSubmitting || isGeneratingAI}
              large
            />
          </div>
        </div>

        {sortedStrategies.length > 0 ? (
          renderStrategyList("Previous Plans")
        ) : (
          <div className="comments-empty">
            <Empty description="No tax plans yet" />
          </div>
        )}
      </Paper>

      {previewModal.open && (
        <Modal
          open={previewModal.open}
          className="edit-note-modal efiling-preview-modal"
          handleClose={() =>
            isSavingDelivery || isSendingPlan || isUpdating ? null : closePreview()
          }
          maxWidth
          title={null}
        >
          <div className="edit-note-wrapper">
            <div className="edit-note-header">
              <h2 className="edit-note-title">
                {getPlanDisplayName(previewModal.strategy || {})}
              </h2>
              <IconButton
                className="edit-note-close-btn"
                onClick={() =>
                  isSavingDelivery || isSendingPlan || isUpdating
                    ? null
                    : closePreview()
                }
                size="small"
                aria-label="close"
              >
                <CloseIcon />
              </IconButton>
            </div>

            <div className="efiling-preview-body">
              {previewModal.loading ? (
                <div className="efiling-preview-loading">Loading preview…</div>
              ) : previewModal.blobUrl ? (
                <iframe
                  title="Tax plan preview"
                  src={previewModal.blobUrl}
                  className="efiling-preview-frame"
                />
              ) : (
                <div className="efiling-preview-loading">
                  PDF preview is not available.
                </div>
              )}
            </div>

            <div className="tax-plan-preview-actions">
              <Button
                text="Edit"
                handleClick={() => openEditModal(previewModal.strategy)}
                disable={isSavingDelivery || isSendingPlan || isUpdating}
                variant="outline"
              />
              <Button
                text={isSavingDelivery ? "Saving..." : "Save"}
                handleClick={() => handleSaveDelivery(previewModal.strategy)}
                disable={
                  isSavingDelivery ||
                  isSendingPlan ||
                  isUpdating ||
                  getPlanDeliveryStatus(previewModal.strategy) === "saved"
                }
                variant="outline"
              />
              <Button
                text={isSendingPlan ? "Sending..." : "Send"}
                handleClick={() => handleSendDelivery(previewModal.strategy)}
                disable={isSavingDelivery || isSendingPlan || isUpdating}
                large
              />
            </div>
          </div>
        </Modal>
      )}

      {editModal.open && (
        <Modal
          open={editModal.open}
          className="edit-note-modal timesheet-view-modal"
          handleClose={() =>
            isUpdating
              ? null
              : setEditModal({
                  open: false,
                  strategyId: null,
                  planName: "",
                  text: "",
                })
          }
          maxWidth
          title={null}
        >
          <div className="edit-note-wrapper">
            <div className="edit-note-header">
              <h2 className="edit-note-title">Edit tax plan</h2>
              <IconButton
                className="edit-note-close-btn"
                onClick={() =>
                  isUpdating
                    ? null
                    : setEditModal({
                        open: false,
                        strategyId: null,
                        planName: "",
                        text: "",
                      })
                }
                size="small"
                aria-label="close"
              >
                <CloseIcon />
              </IconButton>
            </div>

            <div className="comments-input-section" style={{ marginBottom: 0 }}>
              <label
                className="tax-strategy-field-label"
                htmlFor="tax-strategy-edit-name"
              >
                Plan name
              </label>
              <input
                id="tax-strategy-edit-name"
                className="comments-textarea tax-strategy-name-input"
                type="text"
                placeholder="e.g. 2025 IRA planning"
                value={editModal.planName}
                onChange={(e) =>
                  setEditModal((prev) => ({
                    ...prev,
                    planName: e.target.value,
                  }))
                }
                maxLength={120}
                disabled={isUpdating}
              />
              <label
                className="tax-strategy-field-label"
                htmlFor="tax-strategy-edit-text"
                style={{ marginTop: 12 }}
              >
                Plan
              </label>
              <textarea
                id="tax-strategy-edit-text"
                className="comments-textarea"
                placeholder="Update tax plan..."
                value={editModal.text}
                onChange={(e) =>
                  setEditModal((prev) => ({ ...prev, text: e.target.value }))
                }
                rows={8}
                disabled={isUpdating}
              />
              <p className="tax-strategy-edit-hint">
                Saving regenerates the branded PDF and replaces the previous file.
              </p>
              <div
                className="comments-actions"
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: "10px",
                }}
              >
                <Button
                  text="Cancel"
                  handleClick={() =>
                    setEditModal({
                      open: false,
                      strategyId: null,
                      planName: "",
                      text: "",
                    })
                  }
                  disable={isUpdating}
                  variant="outline"
                />
                <Button
                  text={isUpdating ? "Updating PDF..." : "Save & regenerate PDF"}
                  handleClick={handleSaveEdit}
                  disable={isUpdating}
                  large
                />
              </div>
            </div>
          </div>
        </Modal>
      )}

      {commentsModalUi}
    </div>
  );
};

export default TaxStrategySection;
